import useWeeklyObjectives from "@/modules/planner/queries/use-get-weekly-objetives"
import { useWeeklyObjectivesStore } from "@/modules/planner/stores/use-weekly-objectives-store"
import type { Objective } from "@/services/api/cronos/weekly-objectives/get"
import { addDays, endOfWeek, isWithinInterval, startOfWeek, toDate } from "date-fns"
import { useEffect, useMemo, useState } from "react"

interface Week {
  id: string
  totalSeconds: number
  week: number
  totalEstimatedDuration: number
  objectives: Objective[]
  startDate: string
  endDate: string
}

export function useGetObjetivesMetrics() {
  const now = new Date()
  const currentStartDate = startOfWeek(now, { weekStartsOn: 1 })
  const currentEndDate = endOfWeek(addDays(currentStartDate, 7), { weekStartsOn: 1 })

  const [currentWeekObjectives, setCurrentWeekObjectives] = useState<Week | null>(null)
  const [previousWeekObjectives, setPreviousWeekObjectives] = useState<Week | null>(null)
  const weeklyObjectivesQuery = useWeeklyObjectives()
  const { weeklyObjectives, setCurrentWeekNumber, setWeeklyObjectives } = useWeeklyObjectivesStore()

  // Calculate current week index
  useEffect(() => {
    if (weeklyObjectives.length > 0) {
      const today = new Date()
      let targetWeekNumber: number | undefined = undefined

      // Sort weeks by start date to make finding the closest/next/previous one easier and consistent
      const sortedWeeks = [...weeklyObjectives].sort(
        (a, b) => toDate(a.startDate).getTime() - toDate(b.startDate).getTime(),
      )

      const currentActualWeek = sortedWeeks.find((week) => {
        const start = toDate(week.startDate)
        const end = toDate(week.endDate)
        return isWithinInterval(today, { start, end })
      })

      if (currentActualWeek) {
        targetWeekNumber = currentActualWeek.week
      } else {
        // Today is not within any defined week interval.
        // Find the index of the first week that starts after today.
        const nextWeekIndex = sortedWeeks.findIndex((week) => toDate(week.startDate) > today)

        if (nextWeekIndex === -1) {
          // No week starts after today, so today is after all weeks. Use the last week.
          if (sortedWeeks.length > 0) {
            targetWeekNumber = sortedWeeks[sortedWeeks.length - 1].week
          }
        } else if (nextWeekIndex === 0) {
          // Today is before the very first week. Use the first week.
          targetWeekNumber = sortedWeeks[0].week
        } else {
          // Today is between sortedWeeks[nextWeekIndex-1] and sortedWeeks[nextWeekIndex].
          // Set to the week number of the earlier week.
          targetWeekNumber = sortedWeeks[nextWeekIndex - 1].week
        }
      }

      if (targetWeekNumber !== undefined) {
        setCurrentWeekNumber(targetWeekNumber)
      }
    }
    // If weeklyObjectives is empty, currentWeekNumber remains unchanged or relies on store default.
  }, [weeklyObjectives, setCurrentWeekNumber])

  useEffect(() => {
    if (weeklyObjectivesQuery.data && weeklyObjectivesQuery.isSuccess) {
      const currentWeek =
        weeklyObjectivesQuery.data.weeks.find((week) =>
          isWithinInterval(week.startDate, {
            start: currentStartDate,
            end: currentEndDate,
          }),
        ) ?? null

      if (!currentWeek) {
        return
      }
      const previousWeek =
        weeklyObjectivesQuery.data.weeks.find((week) => week.week === currentWeek.week - 1) ?? null

      setCurrentWeekObjectives(currentWeek)
      setPreviousWeekObjectives(previousWeek)
      setWeeklyObjectives(weeklyObjectivesQuery.data.weeks)
    }
  }, [weeklyObjectivesQuery.data, weeklyObjectivesQuery.isSuccess])

  const currentApprovedCount = useMemo(() => {
    if (!currentWeekObjectives) return 0
    return currentWeekObjectives.objectives.filter((obj) => obj.status === "approved").length
  }, [currentWeekObjectives])

  const previousApprovedCount = useMemo(() => {
    if (!previousWeekObjectives) return 0
    return previousWeekObjectives.objectives.filter((obj) => obj.status === "approved").length
  }, [previousWeekObjectives])

  const percentage = useMemo(() => {
    if (!previousApprovedCount || currentApprovedCount === 0) return 0
    return ((currentApprovedCount - previousApprovedCount) / previousApprovedCount) * 100
  }, [currentApprovedCount, previousApprovedCount])

  return {
    currentApprovalCount: currentApprovedCount,
    percentage,
    isPending: weeklyObjectivesQuery.isPending,
  }
}
